import { HStack } from "@repo/components/src/ui/hstack";
import { Spinner } from "@repo/components/src/ui/spinner";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { ComponentProps, useEffect, useMemo, useRef } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import useAddressLevelAreas from "../../hooks/area/useAddressLevelAreas";
import useRegions from "../../hooks/area/useRegions";
import FormInputSelect from "../select";
import FormInputText from "../text";
import {
  AddressFormat,
  AddressFormValues,
  buildAreaOptions,
  getAddressLevel,
  getRegionLevel,
  hasAreaBelowRegion,
} from "./helpers";

type Props = {
  formProps: UseFormReturn<AddressFormValues>;
  countryCode: string;
  format: AddressFormat | null;
  formatLoading?: boolean;
  size?: ComponentProps<typeof FormInputText>["size"];
};

/**
 * Address fields driven by the country's area tree: a region select, a select
 * of the address-level areas inside it (or a free-text city when the country
 * has no deeper levels), then the street parts as free text.
 */
export default function AddressFormFields(props: Props) {
  const { formProps, countryCode, format, formatLoading, size = "md" } = props;
  const { control, setValue } = formProps;
  const regionId = useWatch({ control, name: "regionId" });
  const belowRegion = hasAreaBelowRegion(format);
  const regionLevel = getRegionLevel(format);
  const addressLevel = getAddressLevel(format);

  const { regions } = useRegions(format ? countryCode : null);
  const regionOptions = useMemo(() => buildAreaOptions(regions), [regions]);
  const regionIds = useMemo(() => (regionId ? [regionId] : []), [regionId]);
  const { areas, loading: areasLoading } = useAddressLevelAreas(
    format ? countryCode : null,
    regionIds,
  );
  const areaOptions = useMemo(() => buildAreaOptions(areas), [areas]);

  // The area belongs to the region it was picked in, so it is cleared when
  // the region changes. Not on mount: a prefilled or remounted form keeps it.
  const previousRegionId = useRef(regionId);
  useEffect(() => {
    if (previousRegionId.current === regionId) return;
    previousRegionId.current = regionId;
    setValue("areaId", "");
  }, [regionId, setValue]);

  if (formatLoading) {
    return <Spinner size="small" />;
  }
  if (!format || !regionLevel) {
    return (
      <Text size="sm" className="text-typography-500">
        Aún no podemos registrar direcciones en este país.
      </Text>
    );
  }

  return (
    <VStack space="md">
      <FormInputSelect
        control={control}
        name="regionId"
        label={regionLevel.name}
        placeholder={`Selecciona ${regionLevel.name.toLowerCase()}`}
        options={regionOptions}
        size={size}
        required
      />
      {belowRegion && addressLevel && !!regionId && (
        <FormInputSelect
          key={regionId}
          control={control}
          name="areaId"
          label={addressLevel.name}
          placeholder={
            areasLoading
              ? "Cargando..."
              : `Selecciona ${addressLevel.name.toLowerCase()}`
          }
          options={areaOptions}
          size={size}
          disabled={areasLoading}
          required
        />
      )}
      {!belowRegion && (
        <FormInputText
          control={control}
          name="city"
          label="Ciudad"
          placeholder="Ciudad"
          keyboardType="default"
          textContentType="addressCity"
          autoCapitalize="words"
          size={size}
          required
        />
      )}
      <FormInputText
        control={control}
        name="route"
        label="Calle"
        placeholder="Nombre de la calle"
        keyboardType="default"
        textContentType="streetAddressLine1"
        autoCapitalize="words"
        size={size}
        required
      />
      <HStack space="md">
        <FormInputText
          control={control}
          name="streetNumber"
          label="Número"
          placeholder="Número"
          keyboardType="default"
          textContentType="none"
          autoCapitalize="none"
          size={size}
          className="flex-1"
          required
        />
        <FormInputText
          control={control}
          name="apartment"
          label="Depto/casa"
          placeholder="Depto/casa"
          keyboardType="default"
          textContentType="streetAddressLine2"
          autoCapitalize="sentences"
          size={size}
          className="flex-1"
        />
      </HStack>
    </VStack>
  );
}
